import React, { useRef, useEffect, useState } from 'react';
import { useA11y } from '@context/Utils';
import { TariffProduct } from '@part/Tariff/TariffProduct';
import './TariffSliderAccessible.scss';

export const TariffSliderAccessible = ({ bookableTariffs, setTariffActivationForm }) => {
  const dotsRef = useRef(null);
  const sliderRef = useRef(null);
  const slideRefs = useRef([]);
  const touchStartX = useRef(null);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [slideWidth, setSlideWidth] = useState(0);
  const { tA11yTranslate } = useA11y();

  useEffect(() => {
    // Dynamically measure slide width
    if (sliderRef.current) {
      const firstSlide = sliderRef.current.querySelector('.bfsg-tariff-slide');
      if (firstSlide) {
        const computedStyles = window.getComputedStyle(firstSlide);
        const width = parseFloat(computedStyles.width); // includes bfsg-card-wrapper padding
        const gap = parseInt(computedStyles.marginRight, 10) || 0;
        setSlideWidth(width + gap);
      }
    }
  }, [bookableTariffs]);

  useEffect(() => {
    if (bookableTariffs && bookableTariffs.length > 0) {
      const {
        id = 0,
        name = '',
        nameSpeed = '',
        additionalInfo: { primaryColor } = { primaryColor: '' }
      } = bookableTariffs[currentSlide];
      setTariffActivationForm({
        chosenTariffId: id,
        chosenTariffName: name,
        chosenTariffSpeed: nameSpeed,
        tariffColor: primaryColor
      });
    }
  }, [currentSlide, bookableTariffs]);

  // Important for proper focusing
  useEffect(() => {
    if (slideRefs.current[currentSlide]) {
      slideRefs.current[currentSlide].focus();
    }
  }, [currentSlide]);

  const handleCardClick = (index) => {
    setCurrentSlide(index); // Set the clicked slide as the current slide
  };

  const handleKeyDown = (e) => {
    if (e.key === 'ArrowRight') {
      setCurrentSlide((prev) => (prev + 1) % bookableTariffs.length); // Move to the next slide
    } else if (e.key === 'ArrowLeft') {
      setCurrentSlide((prev) => (prev - 1 + bookableTariffs.length) % bookableTariffs.length); // Move to the previous slide
    }
  };

  // Swipe functionality for touch devices
  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = (e) => {
    if (touchStartX.current === null) return;
    const deltaX = e.changedTouches[0].clientX - touchStartX.current;

    // Threshold for swipe detection
    if (Math.abs(deltaX) > 50) {
      // Infinite swiping
      if (deltaX > 0) {
        // Swipe right
        setCurrentSlide((prev) => (prev - 1 + bookableTariffs.length) % bookableTariffs.length);
      } else {
        // Swipe left
        setCurrentSlide((prev) => (prev + 1) % bookableTariffs.length);
      }

      // Limit swiping to the first and last slide
      // setCurrentSlide((prev) => {
      //   if (deltaX > 0) {
      //     // Swipe right
      //     return prev > 0 ? prev - 1 : prev;
      //     // Swipe left
      //     return prev < bookableTariffs.length - 1 ? prev + 1 : prev;
      //   }
      // });
    }
    touchStartX.current = null;
  };

  if (bookableTariffs.length === 1) {
    const tariff = bookableTariffs[0];
    return (
      <div className="d-flex justify-content-center align-items-center">
        <TariffProduct tariff={tariff} isActiveCard={true} />
      </div>
    );
  }
  return (
    <div className="bfsg-tariff-slider pb-4">
      {/* Dots navigation */}
      <ul className="bfsg-slider-dots" ref={dotsRef}>
        {bookableTariffs.map((_, index) => (
          <li
            key={`dot-${index}`}
            className={`dot ${currentSlide === index ? 'active' : ''}`}
            aria-label={`${tA11yTranslate('ek_slider-tariff_active-index_aria').ariaLabel} ${
              index + 1
            }`}
            aria-selected={currentSlide === index}
            onClick={() => handleCardClick(index)}
            aria-hidden={true}
          />
        ))}
      </ul>
      {/* Slides */}
      <div className="bfsg-slider-list">
        <ul
          ref={sliderRef}
          role="listbox"
          aria-label={tA11yTranslate('ek_slider-tariff_list_aria').ariaLabel}
          tabIndex="0"
          aria-activedescendant={`tariff-activation-product-${currentSlide}`}
          className="bfsg-slider-track"
          onKeyDown={handleKeyDown}
          onTouchStart={handleTouchStart} // Swipe functionality for touch devices
          onTouchEnd={handleTouchEnd} // Swipe functionality for touch devices
        >
          {bookableTariffs.map((tariff, index) => {
            const isActiveCard = index === currentSlide;

            return (
              <li
                className="slider-item"
                key={`tariff-activation-product-${tariff.id}`}
                id={`tariff-activation-product-${index}`}
                role="option"
                aria-selected={currentSlide === index}
                onClick={() => handleCardClick(index)}
                style={{
                  transform: `translateX(-${currentSlide * slideWidth}px)`, // Adjust for active card position
                  transition: 'transform 0.5s ease'
                }}
                // Important for proper focusing
                ref={(el) => (slideRefs.current[index] = el)} // Assign ref to each slide
                // aria-hidden={index !== currentSlide} // Hide inactive slides from screen readers // this will cause warning: Blocked aria-hidden on an element because its descendant retained focus. The focus must not be hidden from assistive technology users.
                tabIndex={index === currentSlide ? 0 : -1} // Make the active card focusable
                // issue: Tab not working
                // onFocus={() => {
                //   // Ensure focus is explicitly set for screen readers
                //   if (slideRefs.current[index]) {
                //     slideRefs.current[index].focus();
                //   }
                // }}
              >
                <div className={`bfsg-tariff-slide ${currentSlide === index ? 'active' : ''}`}>
                  <div>
                    <div className="bfsg-card-wrapper">
                      <TariffProduct key={index} tariff={tariff} isActiveCard={isActiveCard} />
                    </div>
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
};

export default TariffSliderAccessible;
